import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { assetsApi } from '../../services/assets';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/Pagination';

function AssetList() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [assets, setAssets] = useState([]);
  const { pagination, handlePageChange, updatePagination } = usePagination();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await assetsApi.getAll({ page: pagination.page, limit: pagination.limit });
      const assetsData = response.data || response;
      setAssets(Array.isArray(assetsData) ? assetsData : []);
      updatePagination(response.pagination);
    } catch (err) {
      setError('Failed to load assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setHasSearched(false);
      fetchAssets();
      return;
    }

    try {
      setSearching(true);
      setLoading(true);
      setError('');
      const response = await assetsApi.searchByEmployee(searchQuery);
      const data = response.data || response;
      setAssets(Array.isArray(data) ? data : []);
      setHasSearched(true);
    } catch (err) {
      setError('Search failed');
      setAssets([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [searchQuery]);

  const handleClear = () => {
    setSearchQuery('');
    setFilter('all');
    setAssetTypeFilter('all');
    setHasSearched(false);
    fetchAssets();
  };

  useEffect(() => {
    if (!hasSearched) {
      fetchAssets();
    }
  }, [pagination.page, hasSearched]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleExportCSV = async () => {
    try {
      const response = await assetsApi.exportCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export CSV');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await assetsApi.delete(id);
      fetchAssets();
    } catch (err) {
      setError('Failed to delete asset');
    }
  };

  const handleReturn = async (id) => {
    try {
      await assetsApi.return(id);
      fetchAssets();
    } catch (err) {
      setError('Failed to return asset');
    }
  };

  const filteredAssets = assets.filter(asset => {
    const statusMatch = filter === 'all' || asset.status === filter;
    const typeMatch = assetTypeFilter === 'all' || asset.asset_type === assetTypeFilter;
    return statusMatch && typeMatch;
  });

  const getStatusBadge = (status) => {
    const styles = {
      assigned: 'status-assigned',
      available: 'status-available',
      retired: 'status-retired',
      repair: 'status-repair'
    };
    return <span className={`status-badge ${styles[status] || ''}`}>{status}</span>;
  };

  const getAntivirusBadge = (date) => {
    return date 
      ? <span className="badge-success">{new Date(date).toLocaleDateString()}</span>
      : <span className="badge-warning">Not updated</span>;
  };

  if (loading) return <div className="loading">Loading assets...</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Asset Management</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            placeholder="Search by employee name, email or ID..."
            className="form-control form-control-compact-lg"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="form-control form-control-compact-sm">
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="company">Company Asset</option>
          </select>
          <select value={assetTypeFilter} onChange={(e) => setAssetTypeFilter(e.target.value)} className="form-control form-control-compact-sm">
            <option value="all">All Types</option>
            <option value="laptop">Laptop</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
            <option value="other">Other</option>
          </select>
          {(searchQuery || filter !== 'all' || assetTypeFilter !== 'all') && (
            <button className="btn btn-secondary btn-compact" onClick={handleClear}>
              Clear
            </button>
          )}
          {hasRole('super_admin', 'admin_head', 'hr_head') && (
            <button className="btn btn-secondary btn-compact" onClick={handleExportCSV}>
              Export CSV
            </button>
          )}
          {hasRole('super_admin', 'admin_head', 'hr_head', 'employee') && (
            <button className="btn btn-primary btn-compact" onClick={() => navigate('/assets/new')}>
              + Add Asset
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Make / Model</th>
                <th>Serial</th>
                <th>OS</th>
                <th>Antivirus</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
          <tbody>
            {filteredAssets.map(asset => (
              <tr key={asset.id}>
                <td>{asset.asset_type}</td>
                <td>{asset.make || asset.model ? `${asset.make || ''} ${asset.model || ''}`.trim() : '-'}</td>
                <td>{asset.serial_number || '-'}</td>
                <td>{asset.os} {asset.os_version}</td>
                <td>{getAntivirusBadge(asset.antivirus_updated_date)}</td>
                <td>{getStatusBadge(asset.status)}</td>
                <td>{asset.assigned_to_name || '-'}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon btn-view" 
                      onClick={() => navigate(`/assets/${asset.id}`)}
                      title="View"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    {hasRole('super_admin', 'admin_head', 'hr_head') && (
                      <button 
                        className="btn-icon btn-edit" 
                        onClick={() => navigate(`/assets/${asset.id}/edit`)}
                        title="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                    )}
                    {asset.status === 'assigned' && hasRole('super_admin', 'admin_head', 'hr_head') && (
                      <button 
                        className="btn-icon btn-return" 
                        onClick={() => handleReturn(asset.id)}
                        title="Return"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/></svg>
                      </button>
                    )}
                    {hasRole('super_admin') && (
                      <button 
                        className="btn-icon btn-delete" 
                        onClick={() => handleDelete(asset.id)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        {filteredAssets.length === 0 && (
          <div className="empty-state">No assets found</div>
        )}
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      </div>
    </div>
  );
}

export default AssetList;
